import { get } from 'idb-keyval';
import { getActions } from '../global';

import { REST_SESSION_IDB_KEY_PREFIX } from '../config';
import { REST_BACKEND_URL } from '../api/rest/config';
import { ACCOUNT_SLOT } from './multiaccount';
import { parseInitialLocationHash } from './routing';

// Approve side of QR device-login. When an already-authenticated device opens a scanned QR-login
// URL (`origin/#?qrApprove=<token>`), it confirms (via an in-app modal) and approves the other
// device's login by calling the backend with this device's session token. Self-contained on the
// main thread (reads the session straight from IndexedDB) so it needs no worker/callApi plumbing.

type PersistedSession = { token: string };
type QrStatus = { status: 'pending' | 'approved' | 'expired'; device?: { platform: string; ip: string } };

// Must match the key the worker writes (`api/rest/session.ts`): the prefix already ends in `-`, and
// the slot is always spelled out. The bare `rest-session` this once read matches no slot's key, so
// the token never loaded and the approval flow silently did nothing on every account.
const SESSION_IDB_KEY = `${REST_SESSION_IDB_KEY_PREFIX}${ACCOUNT_SLOT || 1}`;

// Read the token from the initial-location params captured at boot by `routing`. We can't read
// `window.location.hash` directly here: `parseInitialLocationHash` strips the hash early in startup
// (before this flow runs), but it preserves the parsed params, so go through it.
function getQrApproveToken(): string | undefined {
  const token = parseInitialLocationHash()?.qrApprove;
  return token ? decodeURIComponent(token) : undefined;
}

async function readSessionToken(): Promise<string | undefined> {
  const saved = await get<PersistedSession>(SESSION_IDB_KEY).catch(() => undefined);
  return saved?.token;
}

async function postJson(path: string, body: unknown, token?: string): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${REST_BACKEND_URL}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
  return res.ok ? res.json() : undefined;
}

// Run at boot: if this device opened a scanned QR-login URL and is itself logged in, fetch the
// requesting device's info and open the in-app approval modal. No-op otherwise.
export async function runQrApproveFlow(): Promise<void> {
  const token = getQrApproveToken();
  if (!token || !REST_BACKEND_URL) return;

  const sessionToken = await readSessionToken();
  if (!sessionToken) return; // not logged in here — can't approve

  const status = (await postJson('/auth/qr/status', { token })) as QrStatus | undefined;
  if (!status || status.status !== 'pending') return; // expired/already used — nothing to confirm

  getActions().openQrApproveModal({ token, platform: status.device?.platform, ip: status.device?.ip });
}

// Approve a pending QR login with this device's session token. Returns true on success.
export async function approveQrToken(token: string): Promise<boolean> {
  const sessionToken = await readSessionToken();
  if (!sessionToken) return false;
  const result = await postJson('/auth/qr/approve', { token }, sessionToken);
  return Boolean(result?.ok);
}
